import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ResultDrawerViewModel, ScoreStatus } from './result-drawer-view-model';
import { RoomDisplayResult } from 'src/app/shared/types/room-display-result';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { ProfileService } from 'src/app/core/services/profile/profile.service';
import { UxGameService } from 'src/app/core/services/ux-game/ux-game.service';
import { safeModalDismiss } from 'src/app/shared/utils/modal.utils';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { launchConfetti } from '../../game-result/confetti';
import { TouchBlocker } from 'src/app/shared/utils/touch-blocker';
import { PlayerStatsDrawerComponent } from '../player-stats-drawer/player-stats-drawer.component';

@Component({
  selector: 'app-result-drawer',
  templateUrl: './result-drawer.component.html',
  styleUrls: ['./result-drawer.component.scss'],
})
export class ResultDrawerComponent implements OnInit, OnDestroy {
  @Input({ required: true }) viewModel!: ResultDrawerViewModel;
  @Input({ required: true }) modalWillDismissCallback!: (callback: () => void) => void;
  @Input() isAfterGame = false;
  @Input() isFreeGame = false;
  @Input() worldWide = false;

  private modalController = inject(ModalController);
  private platform = inject(Platform);
  private profileService = inject(ProfileService);
  private uxGameService = inject(UxGameService);
  private analyticsService = inject(AnalyticsService);

  private touchBlocker = new TouchBlocker();
  themeService = inject(ThemeService);

  OperatorService = OperatorService;
  ScoreStatus = ScoreStatus;

  drawerConfig = this.themeService.drawerConfig();
  currencyIcon?: string;
  currentUserId!: string;

  get glowClass(): string[] {
    return [
      this.themeService.drawerConfig().gradient === 'circle' ? 'glow-circle' : 'glow-linear',
      this.viewModel.getGlowCss(),
    ];
  }

  get drawerStyles(): [string, string][] {
    const key = 'drawer';
    return [
      ['background', key],
      ['border-radius-top', key],
    ];
  }

  get bodyStyles(): [string, string][] {
    return this.getStylesFor('body');
  }

  get playerStyles(): [string, string][] {
    const player = this.getStylesFor('player');
    player.push(['text-decoration', `drawer-result-${this.getStyleKeyForStatus()}-player`]);
    return player;
  }

  get scoreCardStyles(): [string, string][] {
    const key = 'card-score';
    return [
      ['background', key],
      ['border-radius', key],
      ['border', key],
    ];
  }

  get scoreStatusStyles(): [string, string][] {
    const key = 'card-score-amount';
    return [
      ['type', key],
      ['text-color', key],
    ];
  }

  get matchIdStyles(): [string, string][] {
    return this.getStylesFor('match-id');
  }

  get asciiStyles(): [string, string][] {
    const key = `drawer-result-${this.getStyleKeyForStatus()}-amount`;
    return [['text-color', key]];
  }

  get amountStyles(): [string, string][] {
    return this.getStylesFor('amount');
  }

  get asciiIconColor(): string {
    return `var(--drawer-result-${this.getStyleKeyForStatus()}-amount-fill)`;
  }

  get isInverted(): boolean {
    return this.drawerConfig.closeButtonInverted;
  }

  private readonly resultEventMap: Record<RoomDisplayResult, EventName> = {
    [RoomDisplayResult.Win]: EventName.ResultDrawerWinViewed,
    [RoomDisplayResult.Lose]: EventName.ResultDrawerLossViewed,
    [RoomDisplayResult.Draw]: EventName.ResultDrawerDrawViewed,
    [RoomDisplayResult.Cancelled]: EventName.ResultDrawerCancelledViewed,
    [RoomDisplayResult.InProgress]: EventName.ResultDrawerInProgressViewed,
  };

  private logResultEvent(result: RoomDisplayResult): void {
    const eventName = this.resultEventMap[result];
    if (eventName) {
      this.analyticsService.log(eventName);
    } else {
      this.analyticsService.errorWarning(`RoomDisplayResult not implemented: ${result}`);
    }
  }

  async ngOnInit(): Promise<void> {
    this.modalWillDismissCallback?.(() => this.touchBlocker.setDismissing());

    this.currentUserId = this.profileService.userId;
    this.currencyIcon = this.themeService.currencyIcon();
    this.isFreeGame = this.viewModel.isFree;

    if (this.viewModel.roomResult === RoomDisplayResult.Win) {
      launchConfetti(this.themeService);
    }

    this.logResultEvent(this.viewModel.roomResult);
  }

  ngOnDestroy(): void {
    this.touchBlocker.destroy();
  }

  get currencyAmount(): string | undefined {
    return this.viewModel.prize;
  }

  get wonBountyAmount(): number | undefined {
    if (this.viewModel.wonBountyAmount !== undefined) {
      return this.viewModel.wonBountyAmount / 100;
    }
    return undefined;
  }

  async canDismiss(data?: unknown, role?: string) {
    return role !== 'gesture';
  }

  async onCloseClick(): Promise<void> {
    await safeModalDismiss(this.modalController);
    if (this.isAfterGame) {
      this.uxGameService.resetToLoading();
    }
  }

  async onFinishClick(): Promise<void> {
    await safeModalDismiss(this.modalController);
    this.uxGameService.resetToLoading();
  }

  async onPlayerClick(userId: string | null | undefined): Promise<void> {
    if (userId) {
      const isMobile = this.platform.is('mobile');
      let modalWillDismiss: () => void;

      const modal = await this.modalController.create({
        component: PlayerStatsDrawerComponent,
        componentProps: {
          userId,
          gameId: this.viewModel.gameId,
          modalWillDismissCallback: (callback: () => void) => {
            modalWillDismiss = callback;
          },
        },
        initialBreakpoint: isMobile ? 1 : undefined,
        breakpoints: isMobile ? [0, 1] : undefined,
        handle: false,
        backdropDismiss: false,
        canDismiss: async (_, role) => role !== 'gesture',
        cssClass: isMobile ? 'mobile-rounded-modal' : 'desktop-rounded-modal',
      });

      modal.onWillDismiss().then(() => {
        modalWillDismiss?.();
      });

      await modal.present();
    }
  }

  isMobile(): boolean {
    return this.platform.is('mobile');
  }

  private getStyleKeyForStatus() {
    switch (this.viewModel.roomResult) {
      case RoomDisplayResult.Win:
        return 'win';
      case RoomDisplayResult.Lose:
        return 'lose';
      case RoomDisplayResult.Draw:
        return 'draw';
      case RoomDisplayResult.Cancelled:
        return 'cancelled';
      case RoomDisplayResult.InProgress:
        return 'check';
      default:
        return 'cancelled';
    }
  }

  private getStylesFor(suffix: string): [string, string][] {
    const key = `drawer-result-${this.getStyleKeyForStatus()}-${suffix}`;
    return [
      ['type', key],
      ['text-color', key],
    ];
  }
}
